/**
 * Kanban Board — Trello-like view of jobs grouped by status.
 *
 * Workers: see only their assigned jobs. Admin/Supervisor: see all company jobs.
 * Drag-and-drop a card between columns to update its status via /api/jobs/:id/sessions.
 *
 * Designed to feel responsive: optimistic UI updates, server sync on drop.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ClipboardList, MapPin, User as UserIcon, Calendar, RefreshCw, ArrowLeft, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import JobPlannerDialog from '../components/JobPlannerDialog';
import QuickJobNoteDialog from '../components/QuickJobNoteDialog';
import SafetyAttestationDialog from '../components/SafetyAttestationDialog';
import { useAuth } from '../context/AuthContext';
import { JobScopeToggle } from '../components/jobs/JobScopeToggle';
import { TeamViaBadge } from '../components/jobs/TeamViaBadge';
import { getJobScope, saveJobScope, hasJobScopePref } from '../utils/jobScope';
import { useCompanyDefaults, readCompanyDefaults } from '../hooks/useCompanyDefaults';

const API = `${window.__BACKEND_URL__}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const COLUMNS = [
  { id: 'todo', title: 'To Do', tint: 'border-slate-300 dark:border-slate-700', badge: 'bg-slate-500' },
  { id: 'in_progress', title: 'In Progress', tint: 'border-emerald-300 dark:border-emerald-700', badge: 'bg-emerald-500' },
  { id: 'paused', title: 'Paused', tint: 'border-amber-300 dark:border-amber-700', badge: 'bg-amber-500' },
  { id: 'completed', title: 'Completed', tint: 'border-blue-300 dark:border-blue-700', badge: 'bg-blue-500' },
];

const STATUS_TO_ACTION = {
  in_progress: 'start',
  paused: 'pause',
  completed: 'stop',
  todo: null, // can't drag back to "to do" — column is the start state
};

export default function JobsKanban() {
  const navigate = useNavigate();
  const [columns, setColumns] = useState({ todo: [], in_progress: [], paused: [], completed: [] });
  const [loading, setLoading] = useState(true);
  const [draggedJob, setDraggedJob] = useState(null);
  const [plannerJob, setPlannerJob] = useState(null);
  const [quickNoteJob, setQuickNoteJob] = useState(null);
  // Pending drag → in_progress that needs safety attestation before commit.
  const [safetyPending, setSafetyPending] = useState(null);
  const { user, token } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.is_super_user === true;
  const { defaults: companyDefaults, loaded: defaultsLoaded } = useCompanyDefaults(token);
  const [scope, setScope] = useState(() => getJobScope(readCompanyDefaults().jobs_default_scope));
  const changeScope = (next) => { saveJobScope(next); setScope(next); };
  useEffect(() => {
    if (defaultsLoaded && !isAdmin && !hasJobScopePref()) setScope(companyDefaults.jobs_default_scope === 'team' ? 'team' : 'mine');
  }, [defaultsLoaded, companyDefaults.jobs_default_scope, isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/jobs/board`, { headers: authHeaders(), params: isAdmin ? {} : { scope } });
      setColumns(r.data.columns || { todo: [], in_progress: [], paused: [], completed: [] });
    } catch (err) {
      console.error(err);
      toast.error('Failed to load the board');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, scope]);

  useEffect(() => { load(); }, [load]);

  const moveCard = async (job, targetCol, attestation = null) => {
    const action = STATUS_TO_ACTION[targetCol];
    if (!action) {
      toast.info('Jobs flow forward — drop into In Progress, Paused, or Completed');
      return;
    }
    const currentCol = Object.keys(columns).find((k) => columns[k].some((j) => j.id === job.id));
    if (currentCol === targetCol) return;

    // Safety gate: dragging a job INTO "in_progress" for the first time
    // (no prior attestation, not coming from paused) requires the worker
    // to acknowledge the fit-for-duty checklist. Resume (paused → in_progress)
    // re-uses the cached attestation and skips this prompt.
    const isFreshStart = (
      action === 'start'
      && currentCol !== 'paused'
      && !job?.safety_attestation
      && !attestation
    );
    if (isFreshStart) {
      setSafetyPending({ job, targetCol });
      return;
    }

    // Optimistic update.
    setColumns((prev) => {
      const next = { ...prev };
      next[currentCol] = next[currentCol].filter((j) => j.id !== job.id);
      next[targetCol] = [...next[targetCol], { ...job, status: targetCol }];
      return next;
    });

    try {
      const body = {};
      if (attestation && attestation.accepted) {
        body.safety_attestation_accepted = true;
        body.safety_attestation_items = attestation.items;
        body.safety_attestation_version = attestation.version;
      }
      await axios.post(`${API}/jobs/${job.id}/sessions/${action}`, body, { headers: authHeaders() });
      toast.success(`Moved to ${COLUMNS.find((c) => c.id === targetCol).title}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Move failed — refreshing board');
      load();
    }
  };

  const onDragStart = (job) => setDraggedJob(job);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (e, colId) => {
    e.preventDefault();
    if (draggedJob) {
      moveCard(draggedJob, colId);
      setDraggedJob(null);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto" data-testid="jobs-kanban-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/jobs')} className="mb-2" data-testid="kanban-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Jobs
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="w-6 h-6" /> Jobs Board
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Drag a card between columns to update its status. Click "Plan" on any card to organize your work.
          </p>
          {!isAdmin && (
            <div className="mt-3">
              <JobScopeToggle scope={scope} onChange={changeScope} />
            </div>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="kanban-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {COLUMNS.map((col) => (
          <div
            key={col.id}
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, col.id)}
            className={`bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 border-t-4 ${col.tint} min-h-[400px]`}
            data-testid={`kanban-col-${col.id}`}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-sm uppercase tracking-wide">{col.title}</h2>
              <Badge className={`${col.badge} text-white`}>{(columns[col.id] || []).length}</Badge>
            </div>
            <div className="space-y-2">
              {(columns[col.id] || []).map((job) => (
                <Card
                  key={job.id}
                  draggable
                  onDragStart={() => onDragStart(job)}
                  className="p-3 cursor-move hover:shadow-md transition-shadow bg-white dark:bg-slate-800"
                  data-testid={`kanban-card-${job.id}`}
                >
                  <h3 className="font-medium text-sm mb-1 line-clamp-2">{job.title || 'Untitled'}</h3>
                  <TeamViaBadge job={job} className="mb-1" />
                  {job.location_in && (
                    <p className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                      <MapPin className="w-3 h-3" /> <span className="truncate">{job.location_in}</span>
                    </p>
                  )}
                  {job.due_date && (
                    <p className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                      <Calendar className="w-3 h-3" /> Due {new Date(job.due_date).toLocaleDateString()}
                    </p>
                  )}
                  {job.assigned_to_name && (
                    <p className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                      <UserIcon className="w-3 h-3" /> {job.assigned_to_name}
                    </p>
                  )}
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-xs"
                      onClick={() => setPlannerJob(job)}
                      data-testid={`kanban-plan-btn-${job.id}`}
                    >
                      📋 Plan
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs px-2 border-amber-500 text-amber-600 dark:text-amber-300 hover:bg-amber-500/10"
                      onClick={() => setQuickNoteJob(job)}
                      data-testid={`kanban-note-btn-${job.id}`}
                      title="Add a note to this job"
                    >
                      <StickyNote className="w-3 h-3" />
                    </Button>
                  </div>
                </Card>
              ))}
              {(columns[col.id] || []).length === 0 && (
                <p className="text-center text-xs text-slate-400 py-6 italic">Nothing here</p>
              )}
            </div>
          </div>
        ))}
      </div>

      <JobPlannerDialog
        jobId={plannerJob?.id}
        jobTitle={plannerJob?.title}
        open={!!plannerJob}
        onOpenChange={(open) => !open && setPlannerJob(null)}
      />

      <QuickJobNoteDialog
        open={!!quickNoteJob}
        onClose={() => setQuickNoteJob(null)}
        job={quickNoteJob}
        token={token}
        currentUser={user}
        onSaved={load}
      />

      {/* Safety attestation — fires when a card is dragged into "In Progress"
          for the first time. Resume from "Paused" keeps the original. */}
      <SafetyAttestationDialog
        open={!!safetyPending}
        jobTitle={safetyPending?.job?.title}
        isTeamJob={!!safetyPending?.job?.assigned_team_id}
        teamName={safetyPending?.job?.assigned_team_name || null}
        onClose={() => setSafetyPending(null)}
        onAccept={async (template) => {
          const ctx = safetyPending;
          setSafetyPending(null);
          if (ctx) await moveCard(ctx.job, ctx.targetCol, template);
        }}
      />
    </div>
  );
}
